import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useAtom, useAtomValue } from 'jotai';
import { Divider, List } from '@mui/material';
import { isLoggedIn } from '@/state/userInfo';
import { GLOBAL_MODALS, globalModals } from '@/state/globalSettings';
import { matchPath } from 'react-router';
import { Box } from 'lucide-react';

import AppMenuItem from '@/components/system/Layout/LayoutWebCore/WebCoreAppMenus/AppMenuItem';
import wowSpaceMenusConfig from './wowSpaceMenusConfig';
import {
  AppMenuGroup,
  IAppMenuItem,
} from '@/components/system/Layout/LayoutWebCore/WebCoreAppMenus';

export interface WoWSpaceMenusProps {
  menus?: Array<IAppMenuItem | AppMenuGroup | string>;
  open: boolean;
}

const WoWSpaceAppMenuGroup = ({
  group,
  open,
  expandedSubMenus = false,
  onMenuItemClick,
}: {
  group: AppMenuGroup;
  open: boolean;
  expandedSubMenus?: boolean;
  onMenuItemClick?: (item: IAppMenuItem) => void;
}) => {
  const loggedIn = useAtomValue(isLoggedIn);
  const router = useRouter();
  const { pathname = '/' } = router;
  const isActive =
    group?.pathPattern && !!matchPath(group?.pathPattern, pathname || '');
  const [, setModals] = useAtom(globalModals);
  const [expanded, setExpanded] = useState(expandedSubMenus || isActive);
  const hasSubMenus = group?.menus?.length > 0;
  const groupSubMenus = group?.menus?.filter((item) => {
    // @ts-ignore
    return !item?.hidden;
  });

  const handleGroupClick = () => {
    if (group?.requireAuth && !loggedIn) {
      setModals({ [GLOBAL_MODALS.LOGIN]: true });
      return;
    }

    if (group?.onClick) {
      group.onClick();
      return;
    }

    if (hasSubMenus) {
      setExpanded(!expanded);
    } else if (group?.href) {
      router.push(group.href);
    }
    return;
  };

  const handleMenuItemClick = (item: IAppMenuItem) => {
    if (item?.requireAuth && !loggedIn) {
      setModals({ [GLOBAL_MODALS.LOGIN]: true });
      return;
    }

    if (item?.onClick) {
      item.onClick();
    } else if (item?.href) {
      router.push(item.href);
    }

    onMenuItemClick?.(item);
  };

  return (
    <>
      {open && (
        <AppMenuItem
          open={open}
          item={{
            ...group,
            title: group.groupTitle,
          }}
          isGroup={true}
          isActive={isActive}
          isExpanded={expanded}
          hasSubMenus={hasSubMenus}
          onMenuItemClick={handleGroupClick}
        />
      )}

      {expanded && hasSubMenus && (
        <List sx={{ ml: open ? 1 : 0, py: 0 }}>
          {groupSubMenus?.map((item, index) => {
            if (typeof item === 'string' && item === 'divider') {
              return <Divider key={index} sx={{ my: 2 }} />;
            }

            if ('groupTitle' in item) {
              return (
                <WoWSpaceAppMenuGroup
                  key={index}
                  group={item}
                  open={open}
                  onMenuItemClick={onMenuItemClick}
                />
              );
            }

            return (
              <React.Fragment key={index}>
                <AppMenuItem
                  open={open}
                  item={item}
                  onMenuItemClick={() => handleMenuItemClick(item)}
                />
              </React.Fragment>
            );
          })}
        </List>
      )}
    </>
  );
};

export const WoWSpaceAppMenus = ({
  open,
  onMenuItemClick,
}: {
  open: boolean;
  onMenuItemClick?: (menuItem: IAppMenuItem) => void;
}) => {
  const { t } = useTranslation();
  const router = useRouter();

  return (
    <>
      <List>
        {wowSpaceMenusConfig(router, t)
          .filter((item) => {
            if (typeof item === 'string') return true;
            return !item?.hidden;
          })
          .map((item, index) => {
            if (typeof item === 'string' && item === 'divider') {
              return <Divider key={index} sx={{ my: 2 }} />;
            }

            if (typeof item === 'object' && 'groupTitle' in item) {
              return (
                <WoWSpaceAppMenuGroup
                  key={index}
                  group={item}
                  open={open}
                  expandedSubMenus={true}
                  onMenuItemClick={onMenuItemClick}
                />
              );
            }

            return (
              <React.Fragment key={index}>
                <AppMenuItem
                  open={open}
                  item={item as IAppMenuItem}
                  onMenuItemClick={onMenuItemClick}
                />
              </React.Fragment>
            );
          })}
      </List>
    </>
  );
};

export default WoWSpaceAppMenus;
